import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import api from '../../../general/js/api';
import { hasValue } from '../../../general/js/general-helpers';

const MIN_CHARS = 3;
const DEFER_REQUEST_BY = 300;
const BLUR_HIDE_DELAY = 200;
const MAX_HEIGHT = 300;
const NO_SUGGESTION_NOTICE = 'No results found!';
const MORE_SUGGESTIONS_AVAILABLE_CLASS = 'more-suggestions-available';

// inline styles previously applied by devbridge-autocomplete;
// width and offset are owned by predictive-search.scss
const SUGGESTIONS_STYLE = {
    position: 'absolute',
    maxHeight: `${MAX_HEIGHT}px`,
    zIndex: 30,
};

let instanceCounter = 0;

export default class PredictiveSearch extends React.PureComponent {
    constructor(props) {
        super(props);

        this.onSubmit = this.onSubmit.bind(this);
        this.onClearButtonClick = this.onClearButtonClick.bind(this);
        this.inputRef = this.inputRef.bind(this);
        this.formRef = this.formRef.bind(this);
        this.suggestionsRef = this.suggestionsRef.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onSuggestionsMouseDown = this.onSuggestionsMouseDown.bind(this);
        this.onSuggestionsMouseLeave = this.onSuggestionsMouseLeave.bind(this);

        this.baseElement = null;
        this.inputElement = null;
        this.suggestionsElement = null;
        this.lastRequestCancel = null;
        this.deferRequestTimeoutId = null;
        this.blurTimeoutId = null;
        this.isUnmounted = false;
        // value the suggestions list is in sync with, so that a query update
        // caused by our own selection does not trigger another lookup
        this.currentValue = this.props.query || '';
        this.predictiveSearchUrl = this.props.endpointUrl;
        this.maxSuggestionsCount = this.props.maxSuggestionsCount;
        instanceCounter += 1;
        this.listboxId = `predictive-search-suggestions-${instanceCounter}`;

        this.state = {
            suggestions: [],
            selectedIndex: -1,
            isOpen: false,
            isLoading: false,
            hasMoreSuggestions: false,
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.query !== this.props.query && this.props.query !== this.currentValue) {
            this.onValueChange(true);
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true;
        clearTimeout(this.deferRequestTimeoutId);
        clearTimeout(this.blurTimeoutId);
        if (this.lastRequestCancel) {
            this.lastRequestCancel();
            this.lastRequestCancel = null;
        }
    }

    transformSuggestions(result) {
        return {
            suggestions: result.map((suggestion) => {
                const {
                    text, searchType, ...rest
                } = suggestion;
                return {
                    value: text,
                    data: {
                        type: searchType.toString(),
                        ...rest,
                    }
                };
            })
        };
    }

    /**
     * Build suggestion markup
     * Based on the default devbridge-autocomplete method
     *
     * @param suggestion
     * @param currentValue
     * @returns {string}
     */
    formatResult(suggestion, currentValue) {
        let highlightedText = suggestion.value;

        // Do not replace anything if the current value is empty
        if (currentValue) {
            const pattern = '(' + currentValue.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&') + ')';
            highlightedText = highlightedText.replace(new RegExp(pattern, 'gi'), '<strong>$1</strong>');
        }

        return this.props.renderSuggestion ?
            this.props.renderSuggestion(suggestion.value, suggestion.data.type, suggestion.data, highlightedText) :
            highlightedText;
    }

    onValueChange(defer) {
        clearTimeout(this.deferRequestTimeoutId);

        const query = this.props.query || '';
        this.currentValue = query;

        if (query.length < MIN_CHARS) {
            this.hide();
            return;
        }

        if (defer) {
            this.deferRequestTimeoutId = setTimeout(() => this.requestSuggestions(query), DEFER_REQUEST_BY);
        } else {
            this.requestSuggestions(query);
        }
    }

    requestSuggestions(query) {
        this.setState({ isLoading: true });

        this.getSuggestions(query).then((data) => {
            if (this.isUnmounted) return;

            if (document.activeElement === this.inputElement) {
                this.showSuggestions(data.result, data.total, query);
            }
            this.setState({ isLoading: false });
        }, (error) => {
            // hide after error only when our request has been canceled
            if (!this.isUnmounted && !axios.isCancel(error)) {
                this.setState({ isLoading: false });
            }
        });
    }

    showSuggestions(result, total, query) {
        const suggestions = this.transformSuggestions(hasValue(result) ? result : []).suggestions
            .map((suggestion) => ({
                ...suggestion,
                html: this.formatResult(suggestion, query),
            }));

        this.setState({
            suggestions,
            selectedIndex: -1,
            isOpen: true,
            hasMoreSuggestions: total > this.maxSuggestionsCount,
        });
    }

    hide() {
        clearTimeout(this.deferRequestTimeoutId);
        this.setState({ isOpen: false, selectedIndex: -1 });
    }

    getSuggestions(query) {
        if (this.lastRequestCancel) {
            this.lastRequestCancel();
            this.lastRequestCancel = null;
        }
        const CancelToken = axios.CancelToken;
        const source = CancelToken.source();
        this.lastRequestCancel = source.cancel.bind(source);

        return api.get(this.predictiveSearchUrl, {
            params: { term: query },
            cancelToken: source.token
        }).then(
            ({ data }) => data,
            (error) => {
                if (!axios.isCancel(error)) {
                    this.lastRequestCancel = null;
                }
                return Promise.reject(error);
            }
        );
    }

    formRef(el) {
        this.baseElement = el;
    }

    inputRef(el) {
        this.inputElement = el;
        if (this.props.inputRef) {
            this.props.inputRef(el);
        }
    }

    suggestionsRef(el) {
        this.suggestionsElement = el;
    }

    isFilledMode() {
        return this.props.query !== '' && this.props.isSubmitted && !this.props.isFocused;
    }

    onFocus(e) {
        this.props.onFocus(e);

        if ((this.props.query || '').length >= MIN_CHARS) {
            this.onValueChange(false);
        }
    }

    onBlur(e) {
        this.props.onBlur(e);

        // if the user clicked on a suggestion, hiding is canceled by
        // the suggestions container mousedown handler
        clearTimeout(this.blurTimeoutId);
        this.blurTimeoutId = setTimeout(() => {
            if (!this.isUnmounted) {
                this.hide();
            }
        }, BLUR_HIDE_DELAY);
    }

    onKeyDown(e) {
        const { isOpen, selectedIndex, suggestions } = this.state;

        switch (e.key) {
            case 'Escape':
                this.hide();
                break;
            case 'Tab':
                if (selectedIndex === -1) {
                    this.hide();
                    return;
                }
                this.select(selectedIndex);
                // let the focus move on, as devbridge did with tabDisabled: false
                return;
            case 'Enter':
                if (selectedIndex === -1) {
                    this.hide();
                    // no suggestion is highlighted, let the form submit
                    return;
                }
                this.select(selectedIndex);
                break;
            case 'ArrowUp':
                if (!isOpen || selectedIndex === -1) break;
                this.moveSelection(selectedIndex - 1);
                break;
            case 'ArrowDown':
                if (!isOpen || selectedIndex === suggestions.length - 1) break;
                this.moveSelection(selectedIndex + 1);
                break;
            default:
                return;
        }

        e.stopPropagation();
        e.preventDefault();
    }

    moveSelection(index) {
        this.setState({ selectedIndex: index }, () => this.adjustScroll(index));
    }

    adjustScroll(index) {
        const container = this.suggestionsElement;
        if (index === -1 || !container) return;

        const activeItem = container.querySelectorAll('.autocomplete-suggestion')[index];
        if (!activeItem) return;

        const offsetTop = activeItem.offsetTop;
        const heightDelta = activeItem.offsetHeight;
        const upperBound = container.scrollTop;
        const lowerBound = upperBound + MAX_HEIGHT - heightDelta;

        if (offsetTop < upperBound) {
            container.scrollTop = offsetTop;
        } else if (offsetTop > lowerBound) {
            container.scrollTop = offsetTop - MAX_HEIGHT + heightDelta;
        }
    }

    onSuggestionsMouseDown() {
        // keep the list alive long enough for the click to land on a suggestion
        clearTimeout(this.blurTimeoutId);
    }

    onSuggestionsMouseLeave() {
        this.setState({ selectedIndex: -1 });
    }

    select(index) {
        const suggestion = this.state.suggestions[index];
        if (!suggestion) return;

        clearTimeout(this.blurTimeoutId);
        this.currentValue = suggestion.value;
        this.hide();
        this.handlePredictiveSelect(suggestion);
    }

    renderSuggestions() {
        const { isOpen, suggestions, selectedIndex } = this.state;

        if (!isOpen) return null;

        return (
            <div className="autocomplete-suggestions"
                id={this.listboxId}
                role="listbox"
                ref={this.suggestionsRef}
                style={SUGGESTIONS_STYLE}
                max-suggestions-count={this.maxSuggestionsCount}
                onMouseDown={this.onSuggestionsMouseDown}
                onMouseLeave={this.onSuggestionsMouseLeave}>
                {suggestions.length ?
                    suggestions.map((suggestion, index) => (
                        <div key={`${suggestion.data.type}-${suggestion.value}-${index}`}
                            id={`${this.listboxId}-${index}`}
                            role="option"
                            aria-selected={index === selectedIndex}
                            className={`autocomplete-suggestion ${index === selectedIndex ? 'autocomplete-selected' : ''}`}
                            data-index={index}
                            onMouseOver={() => this.setState({ selectedIndex: index })}
                            onClick={() => this.select(index)}
                            dangerouslySetInnerHTML={{ __html: suggestion.html }}>
                        </div>
                    )) :
                    <div className="autocomplete-no-suggestion">{NO_SUGGESTION_NOTICE}</div>
                }
            </div>
        );
    }

    renderInnerButton() {
        if (this.isFilledMode()) {
            return (
                <button onClick={this.onClearButtonClick} type="button"
                    className="predictive-search__inner-button"
                    aria-label="Clear query">
                    <span className="button-content">
                        <svg className="icon" width="10" height="10">
                            <use xlinkHref="#icon-cross"></use>
                        </svg>
                    </span>
                </button>
            );
        }

        return (
            <button onClick={this.props.onSearchButtonClick} type="submit"
                className="predictive-search__inner-button"
                aria-label="Search">
                <span className="button-content">
                    <svg className="icon" width="17" height="17">
                        <use xlinkHref="#icon-search"></use>
                    </svg>
                </span>
            </button>
        );
    }

    render() {
        const isFilledMode = this.isFilledMode();
        const isMinimized = this.props.isMinimized;
        const isExpanded = this.props.isExpanded && !isFilledMode;
        const {
            isOpen, isLoading, hasMoreSuggestions, selectedIndex
        } = this.state;

        return (
            <div
                className={`predictive-search ${(isMinimized && !isExpanded) ? 'is-minimized' : ''} ${isFilledMode ? 'is-filled' : ''}`}>
                <form
                    className={`predictive-search__inner ${hasMoreSuggestions ? MORE_SUGGESTIONS_AVAILABLE_CLASS : ''}`}
                    ref={this.formRef}
                    onSubmit={this.onSubmit}>
                    <input onChange={this.props.onChange}
                        value={this.props.query}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        onKeyDown={this.onKeyDown}
                        ref={this.inputRef}
                        placeholder="Player name or nationality"
                        className="predictive-search__input" type="text"
                        autoComplete="off"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-expanded={isOpen}
                        aria-owns={this.listboxId}
                        aria-activedescendant={(isOpen && selectedIndex !== -1) ? `${this.listboxId}-${selectedIndex}` : null}/>

                    {/* additional input is a workaround to stop form submit after clicking on button[type="button"] */}
                    <input type="hidden"/>
                    <div className={`predictive-search__spinner spinner spinner--blue ${isLoading ? 'is-loading' : ''}`}>
                    </div>
                    {this.renderInnerButton(isFilledMode)}
                    {this.renderSuggestions()}
                </form>
                {
                    (isMinimized && isExpanded) ? (
                        <button onClick={this.props.onCollapseButtonClick} type="button"
                            className="predictive-search__collapse-button">
                            <span className="button-content">
                                <svg className="icon" width="10" height="10">
                                    <use xlinkHref="#icon-cross"></use>
                                </svg>
                            </span>
                        </button>
                    ) : null
                }
            </div>
        );
    }

    onClearButtonClick(e) {
        e.preventDefault();
        this.props.onClear();
    }

    onSubmit(e) {
        e.preventDefault();
        this.props.onSubmit();
    }

    handlePredictiveSelect(suggestion) {
        this.props.onSelect(suggestion.value, suggestion.data.type, suggestion.data);
    }
}

PredictiveSearch.propTypes = {
    endpointUrl: PropTypes.string.isRequired,
    maxSuggestionsCount: PropTypes.number.isRequired,
    renderSuggestion: PropTypes.func,
    onClear: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    query: PropTypes.string,
    onBlur: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    onCollapseButtonClick: PropTypes.func.isRequired,
    onSearchButtonClick: PropTypes.func.isRequired,
    inputRef: PropTypes.func,
    isMinimized: PropTypes.bool.isRequired,
    isSubmitted: PropTypes.bool.isRequired,
    isFocused: PropTypes.bool.isRequired,
    isExpanded: PropTypes.bool.isRequired,
};
